import { useCallback, useRef, useState } from 'react';

/**
 * Utility hook to make writing data request hooks faster
 */
export const useLoadDataRequest = <T>() => {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<unknown>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOrInit, setIsLoadingOrInit] = useState(true);
  const req = useRef<Promise<T>>();

  /**
   * Track the given load request (promise) and update the state
   * with loading flags / return data / error data (when available).
   */
  const load = useCallback(async (thisReq: Promise<T>) => {
    req.current = thisReq;

    try {
      setIsLoading(true);
      setIsLoadingOrInit(true);
      const newData = await thisReq;

      if (thisReq === req.current) {
        setData(newData);
        setError(undefined);
      }
    } catch (e) {
      if (thisReq === req.current) {
        setError(e);
      }
    } finally {
      if (thisReq === req.current) {
        setIsLoading(false);
        setIsLoadingOrInit(false);
      }
    }
  }, []);

  /**
   * Will reset all state back to default values
   */
  const reset = useCallback((isLoadingOrInit = true) => {
    setData(undefined);
    setError(undefined);
    setIsLoadingOrInit(isLoadingOrInit);
    setIsLoading(false);
    req.current = undefined;
  }, []);

  return {
    data,
    error,
    isLoading,
    isLoadingOrInit,
    load,
    reset
  };
};
